import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Loader2, TerminalSquare } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { sendMessageToModel } from '@/services/ModelService';
import type { AIModel, Message } from '@/types';
import {
  SHIMMER_INTERVAL_MS,
  buildSessionMeta,
  buildThinkingFrame,
} from './maplecode/claudeBrowserCompat';
import ConsoleMarkdown from './maplecode/ConsoleMarkdown';
import type { FileSystemNavigator, FileSystemItem } from '../../utils/filesystem';
import { useUserVFS } from '../../hooks/useUserVFS';
import { useUserSettings } from '../../hooks/useUserSettings';

interface MapleCodeREPLProps {
  onExit: () => void;
  navigator?: FileSystemNavigator;
  initialPath?: string;
  initialPrompt?: string;
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  availableTerminalCommands?: string[];
  availablePrograms?: Array<{ id: string; label: string; description?: string }>;
  runTerminalCommand?: (command: string) => Promise<{ kind: 'output'; text: string } | { kind: 'input'; prompt: string }>;
  submitTerminalInput?: (input: string) => Promise<{ kind: 'output'; text: string } | { kind: 'input'; prompt: string }>;
}

type MapleCodeMessage = {
  id: string;
  role: 'assistant' | 'user' | 'system' | 'tool';
  content: string;
  label?: string;
};

type TerminalPaneEntry = {
  id: string;
  kind: 'command' | 'compile' | 'runtime' | 'input';
  content: string;
};

type MapleCodeWasmRunResultDetail = {
  projectPath: string;
  moduleName: string;
  status: 'success' | 'error';
  runId: number;
  lines: string[];
  sourcePath?: string;
};

const TRUST_DEFAULT_KEY = 'trust_mode_enabled';
const BYPASS_DEFAULT_KEY = 'maplecode_bypass_permissions_enabled';
const MAPLEOS_STYLE_DEFAULT_KEY = 'maplecode_mapleos_style_enabled';
const INPUT_HISTORY_KEY = 'maplecode_input_history';
const MAX_INPUT_HISTORY = 100;
const DISPLAY_SETTINGS_DEFAULTS = {
  selectedAccent: 'red',
  isDarkMode: true,
  desktopColor: '200 25% 92%',
  primaryForegroundMode: 'auto',
  accentForegroundMode: 'auto',
  useAccentTitlebar: true,
};

const MAPLEOS_NATIVE_APP_STYLE_GUIDE = [
  'MapleOS native app style guide based on built-in apps like File Explorer, Notepad, and Display Properties:',
  '- App root should usually be a full-height flex column layout using h-full flex flex-col overflow-hidden bg-background.',
  '- Use top chrome sections such as toolbars or header rows with border-b border-border and soft surfaces like bg-muted/50 or bg-muted/30 instead of hardcoded page fills.',
  '- Main content areas should usually be flex-1 min-h-0 overflow-auto with practical padding such as p-4 or p-6.',
  '- Prefer bordered panels, cards, and address/search bars with rounded-md or rounded-xl, bg-background or bg-card/50, and border-border or border-border/50.',
  '- Use accent color sparingly for active states, selected rows, icon chips, primary buttons, and focus rings. Do not flood the whole page with accent or desktop color.',
  '- Common MapleOS patterns include subtle backdrop blur, soft shadows, icon chips like p-2 rounded-lg bg-primary/10 border border-primary/20, and muted helper text via text-muted-foreground.',
  '- Buttons should usually use ghost or outline for toolbar actions and reserve solid primary styling for the main action only.',
  '- Lists, file rows, and content sections should have clear spacing, hover states, and borders rather than giant flat blocks of color.',
  '- Forms should resemble built-in apps: inputs and textareas on bg-background, bordered with border-border, and readable in both dark and light mode.',
  '- Avoid hardcoded width: 100vw, avoid layouts that exceed the window width, use box-sizing: border-box, and prevent accidental horizontal scrolling.',
  '- If you build a notes app or editor, take inspiration from Notepad and File Explorer: a compact top bar or toolbar, a clean writing area, a bordered notes list or file list, and a restrained neutral background driven by MapleOS theme variables.',
].join(' ');

const TOOL_SUMMARY = [
  '/help — show MapleCode commands',
  '/status — show session status',
  '/trust off — disable Trust Mode for MapleOS AI workflows',
  '/bypass on — enable MapleCode Bypass Permissions mode',
  '/bypass off — disable MapleCode Bypass Permissions mode',
  '/os-style on — make MapleCode use current MapleOS display settings for desktop app generation',
  '/os-style off — disable MapleOS-styled desktop app generation hints',
  '/pwd — print the current MapleCode working directory',
  '/ls [path] — list files and folders',
  '/cd <path> — change MapleCode working directory',
  '/read <path> — read a file from the MapleOS virtual file system',
  '/commands — list MapleOS Terminal commands that MapleCode knows about',
  '/apps — list MapleOS command-line launchable apps',
  '/cmd <terminal command> — execute a MapleOS Terminal command from MapleCode',
  '```terminal blocks auto-execute when Trust Mode or Bypass Permissions is on',
  '/run [wasmname] — save the latest ```wat block under a stable module name and run it silently; if wasmname is omitted, MapleCode uses the latest AI-suggested name',
  '/open-app [appname] — save or reopen the latest MapleOS desktop UI app and open it in Module Host',
  '/run-example — legacy alias for /run latest',
  '/wasm-run — legacy alias for /run latest',
  '/clear — clear the MapleCode transcript',
  'exit or Ctrl+C — return to the MapleOS terminal',
];

const MAX_AUTO_TERMINAL_STEPS = 4;

const createId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `maplecode-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const createMessage = (
  role: MapleCodeMessage['role'],
  content: string,
  label?: string,
): MapleCodeMessage => ({
  id: createId(),
  role,
  content,
  label,
});

const getStoredBoolean = (key: string, fallback: boolean) => {
  try {
    const value = localStorage.getItem(key);
    if (value === null) return fallback;
    return value === 'true';
  } catch {
    return fallback;
  }
};

const getStoredStringArray = (key: string) => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return [] as string[];

    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [] as string[];

    return parsed
      .map((entry) => String(entry || '').trim())
      .filter(Boolean)
      .slice(-MAX_INPUT_HISTORY);
  } catch {
    return [] as string[];
  }
};

const persistBoolean = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, value ? 'true' : 'false');
    return true;
  } catch {
    return false;
  }
};

const normalizeWindowsPath = (value: string) => {
  const trimmed = value.trim().replace(/\//g, '\\');
  if (!trimmed) return 'C:\\';

  const normalizedDrive = trimmed.replace(/^c:\\/i, 'C:\\').replace(/\\+$/, '');
  if (/^[A-Z]:$/i.test(normalizedDrive)) {
    return `${normalizedDrive}\\`;
  }

  if (/^[A-Z]:\\/i.test(normalizedDrive)) {
    return normalizedDrive;
  }

  return normalizedDrive.replace(/^\\+/, '');
};

const joinWindowsPath = (base: string, fragment: string) => {
  const normalizedBase = normalizeWindowsPath(base);
  const normalizedFragment = normalizeWindowsPath(fragment).replace(/^C:\\/, '');

  if (!normalizedFragment) return normalizedBase;
  if (normalizedBase === 'C:\\') return `C:\\${normalizedFragment}`;
  return `${normalizedBase}\\${normalizedFragment}`;
};

const formatItemLine = (item: FileSystemItem) => {
  const typeLabel = item.type === 'folder' ? '<DIR>' : item.size || 'file';
  return `${typeLabel.padEnd(12)} ${item.name}`;
};

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary);
};

const extractLatestNamedBlockFromText = (content: string, name: string) => {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const fencedPattern = new RegExp('```(?:' + escapedName + ')\\s*\\n([\\s\\S]*?)```', 'gi');

  const fencedMatches = [...content.matchAll(fencedPattern)];
  const latestFenced = fencedMatches.at(-1);
  if (latestFenced?.[1]?.trim()) {
    return latestFenced[1].trim();
  }

  const lines = content.split(/\r?\n/);
  let latestBoxed: string | null = null;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();
    const startMatch = line.match(/^\+--\[([^\]]+)\]\s*$/);
    if (!startMatch || startMatch[1].trim().toLowerCase() !== name.toLowerCase()) {
      continue;
    }

    const blockLines: string[] = [];
    let cursor = index + 1;
    while (cursor < lines.length) {
      const candidate = lines[cursor];
      if (candidate.trim() === '+--') {
        break;
      }
      blockLines.push(candidate.replace(/^\s*\|\s?/, ''));
      cursor += 1;
    }

    const normalized = blockLines.join('\n').trim();
    if (normalized) {
      latestBoxed = normalized;
    }

    index = cursor;
  }

  return latestBoxed;
};

const extractLatestWatBlock = (messages: MapleCodeMessage[]) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const latestBlock = extractLatestNamedBlockFromText(message.content, 'wat');

    if (latestBlock) {
      return {
        code: latestBlock,
        role: message.role,
        label: message.label || message.role,
      };
    }
  }

  return null;
};

const extractLatestHtmlBlock = (messages: MapleCodeMessage[]) => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const latestBlock = extractLatestNamedBlockFromText(message.content, 'html');

    if (latestBlock) {
      return {
        html: latestBlock,
        role: message.role,
        label: message.label || message.role,
      };
    }
  }

  return null;
};

const extractLatestWatBlockFromText = (content: string, label = 'assistant') => {
  const latestBlock = extractLatestNamedBlockFromText(content, 'wat');

  if (!latestBlock) {
    return null;
  }

  return {
    code: latestBlock,
    role: 'assistant' as const,
    label,
  };
};

const extractLatestHtmlBlockFromText = (content: string, label = 'assistant') => {
  const latestBlock = extractLatestNamedBlockFromText(content, 'html');

  if (!latestBlock) {
    return null;
  }

  return {
    html: latestBlock,
    label,
  };
};

const hasUnclosedNamedFence = (content: string, name: string) => {
  const normalizedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const openingFence = new RegExp('```(?:' + normalizedName + ')\\s*\\n', 'i');
  const openingMatch = content.match(openingFence);
  if (!openingMatch || openingMatch.index === undefined) {
    return false;
  }

  const rest = content.slice(openingMatch.index + openingMatch[0].length);
  return !/\n```/.test(rest);
};

const isLikelyIncompleteHtmlBlock = (html: string) => {
  const trimmed = html.trim();
  if (!trimmed) {
    return true;
  }

  const lower = trimmed.toLowerCase();
  const hasHtmlOpen = lower.includes('<html');
  const hasBodyOpen = lower.includes('<body');

  if (hasHtmlOpen && !lower.includes('</html>')) {
    return true;
  }

  if (hasBodyOpen && !lower.includes('</body>')) {
    return true;
  }

  if (/<script\b/i.test(trimmed) && !/<\/script>/i.test(trimmed)) {
    return true;
  }

  if (/<style\b/i.test(trimmed) && !/<\/style>/i.test(trimmed)) {
    return true;
  }

  return false;
};

const extractLatestTerminalCommandBlockFromText = (content: string, label = 'assistant') => {
  const terminalFence = /```(?:terminal|cmd|mapleos)\s*\n([\s\S]*?)```/gi;
  const matches = [...content.matchAll(terminalFence)];
  const latestMatch = matches.at(-1);

  if (!latestMatch?.[1]?.trim()) {
    return null;
  }

  const raw = latestMatch[1].trim();
  const commands = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^\/cmd\s+/i, ''));

  if (commands.length === 0) {
    return {
      commands: [],
      raw,
      label,
      error: 'MapleCode could not parse any terminal commands from the fenced block.',
    };
  }

  if (commands.length > MAX_AUTO_TERMINAL_STEPS) {
    return {
      commands: [],
      raw,
      label,
      error: `MapleCode only auto-executes up to ${MAX_AUTO_TERMINAL_STEPS} terminal commands per fenced block. Use /cmd for anything longer.`,
    };
  }

  return {
    commands,
    raw,
    label,
    error: null,
  };
};

const extractLatestInputRequestBlockFromText = (content: string, label = 'assistant') => {
  const inputFence = /```input\s*\n([\s\S]*?)```/gi;
  const matches = [...content.matchAll(inputFence)];
  const latestMatch = matches.at(-1);

  if (!latestMatch?.[1]?.trim()) {
    return null;
  }

  return {
    prompt: latestMatch[1].trim(),
    label,
  };
};

const extractLatestWasmNameBlockFromText = (content: string, label = 'assistant') => {
  const latestBlock = extractLatestNamedBlockFromText(content, 'wasmname');

  if (!latestBlock) {
    return null;
  }

  return {
    name: latestBlock.split(/\r?\n/)[0].trim(),
    label,
  };
};

const extractLatestAppNameBlockFromText = (content: string, label = 'assistant') => {
  const latestBlock = extractLatestNamedBlockFromText(content, 'appname');

  if (!latestBlock) {
    return null;
  }

  return {
    name: latestBlock.split(/\r?\n/)[0].trim(),
    label,
  };
};

const formatAutoTerminalToolMessage = (
  command: string,
  result: string,
  approvalMode: 'trust' | 'bypass',
) => {
  const normalizedResult = result.trim() || '<no output>';
  return [
    approvalMode === 'bypass'
      ? 'Auto-approved terminal command via Bypass Permissions.'
      : 'Auto-approved terminal command via Trust Mode.',
    '',
    '```cmd',
    command,
    '```',
    '',
    normalizedResult,
  ].join('\n');
};

const shouldAutoBuildDesktopAppRequest = (prompt: string) => {
  const normalized = prompt.toLowerCase();
  const mentionsDesktopUi = /\b(desktop|windowed|window|ui|interface|frontend|visual|gui)\b/.test(normalized);
  const mentionsWebStack = /\b(html|css|javascript|js|web)\b/.test(normalized);
  const mentionsMapleBridge = /window\.mapleos|\bmapleos api\b/.test(normalized);
  const mentionsApp = /\b(app|application|program|tool|tester|dashboard|editor|viewer|launcher)\b/.test(normalized);
  const terminalOnly = /\bterminal\b/.test(normalized) && !mentionsDesktopUi && !mentionsWebStack;
  const wasmLike = /\b(wat|wasm|webassembly|abi|module host)\b/.test(normalized);

  return !terminalOnly && !wasmLike && (
    (mentionsDesktopUi && mentionsApp)
    || (mentionsWebStack && mentionsApp)
    || mentionsMapleBridge
  );
};

const shouldAutoRunWatRequest = (prompt: string) => {
  const normalized = prompt.toLowerCase();
  const requestsBuild = /\b(build|create|make|generate|write)\b/.test(normalized);
  const requestsCompileOrRun = /\b(compile|run|execute|launch)\b/.test(normalized);
  const optsOutOfExecution = /\b(?:do not|don't|dont|without)\s+(?:compile|run|execute|launch)\b/.test(normalized);
  const desktopUiRequest = shouldAutoBuildDesktopAppRequest(prompt);

  return !desktopUiRequest && !optsOutOfExecution && (requestsBuild || requestsCompileOrRun);
};

const isWatCompileFailureResult = (value: string) => {
  const normalized = value.trim().toLowerCase();
  return normalized.startsWith('auto-compile failed for the wat example.')
    || normalized.startsWith('compilation failed for the wat example.');
};

const requestsUnsupportedRuntimeInput = (content: string) => {
  return /\b(?:env\.|host\.)?input_utf8\b|\bstdin\b|\bscanf\b|\bread_line\b|\bprompt\s*\(/i.test(content);
};

const isRuntimeInputReminderMessage = (message: MapleCodeMessage) => {
  return message.role === 'tool'
    && (message.label || '') === 'input'
    && message.content.startsWith('MapleCode runtime input reminder:');
};

const isInputReplyMessage = (message: MapleCodeMessage) => {
  return message.role === 'tool'
    && (message.label || '') === 'input'
    && message.content.startsWith('Input reply received.');
};

const filterTranscriptForModel = (transcript: MapleCodeMessage[]) => {
  let latestInputReplyIndex = -1;

  for (let index = transcript.length - 1; index >= 0; index -= 1) {
    if (isInputReplyMessage(transcript[index])) {
      latestInputReplyIndex = index;
      break;
    }
  }

  if (latestInputReplyIndex === -1) {
    return transcript;
  }

  return transcript.filter((message, index) => {
    if (!isRuntimeInputReminderMessage(message)) {
      return true;
    }

    return index > latestInputReplyIndex;
  });
};

export const MapleCodeREPL: React.FC<MapleCodeREPLProps> = ({
  onExit,
  navigator,
  initialPath = 'C:\\',
  initialPrompt,
  onOpenWindow,
  availableTerminalCommands = [],
  availablePrograms = [],
  runTerminalCommand,
  submitTerminalInput,
}) => {
  const models = useSupabaseStore((state) => state.models);
  const fetchModels = useSupabaseStore((state) => state.fetchModels);
  const { createFile: createUserFile, getFile: getUserFile, reloadFile: reloadUserFile } = useUserVFS();
  const { settings: displaySettings } = useUserSettings('display', DISPLAY_SETTINGS_DEFAULTS);
  const initialMessagesRef = useRef<MapleCodeMessage[]>([
    createMessage(
      'system',
      [
        'MapleCode started.',
        '',
        'This is a browser-safe Claude Code style REPL for MapleOS.',
        'It keeps the full-screen terminal workflow, supports slash-command tools, and uses your configured AI model for normal prompts.',
        '',
        'Type /help to see the available MapleCode commands.',
      ].join('\n'),
      'session',
    ),
  ]);

  const [messages, setMessages] = useState<MapleCodeMessage[]>(() => initialMessagesRef.current);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [currentPath, setCurrentPath] = useState(initialPath);
  const [trustEnabled, setTrustEnabled] = useState(() => getStoredBoolean(TRUST_DEFAULT_KEY, true));
  const [bypassEnabled, setBypassEnabled] = useState(() => getStoredBoolean(BYPASS_DEFAULT_KEY, false));
  const [mapleOsStyleEnabled, setMapleOsStyleEnabled] = useState(() => getStoredBoolean(MAPLEOS_STYLE_DEFAULT_KEY, false));
  const [thinkingTick, setThinkingTick] = useState(0);
  const [inputHistory, setInputHistory] = useState<string[]>(() => getStoredStringArray(INPUT_HISTORY_KEY));
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [pendingInputPrompt, setPendingInputPrompt] = useState<string | null>(null);
  const [pendingTerminalInputPrompt, setPendingTerminalInputPrompt] = useState<string | null>(null);
  const [isTerminalPaneOpen, setIsTerminalPaneOpen] = useState(false);
  const [terminalPaneEntries, setTerminalPaneEntries] = useState<TerminalPaneEntry[]>([]);

  const sessionStartedAtRef = useRef(Date.now());
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);
  const terminalPaneEndRef = useRef<HTMLDivElement | null>(null);
  const thinkingIntervalRef = useRef<number | null>(null);
  const initialPromptSubmittedRef = useRef(false);
  const historyDraftRef = useRef('');
  const messagesRef = useRef<MapleCodeMessage[]>(initialMessagesRef.current);
  const isLoadingRef = useRef(false);
  const pendingWasmRunRootsRef = useRef<Set<string>>(new Set());
  const autoDebuggedWasmRunRootsRef = useRef<Set<string>>(new Set());
  const pendingTerminalInputShouldResumeAssistantRef = useRef(false);
  const pendingInputContinuationRef = useRef<{ prompt: string; autoRunWat: boolean; autoBuildDesktopApp: boolean } | null>(null);

  const activeModels = useMemo(
    () => models.filter((model) => model.is_active !== false),
    [models],
  );

  const selectedModel = useMemo(
    () => activeModels.find((model) => model.id === selectedModelId) || activeModels[0] || null,
    [activeModels, selectedModelId],
  );

  const sessionMeta = useMemo(
    () =>
      buildSessionMeta({
        startedAt: sessionStartedAtRef.current,
        messageCount: messages.length,
        currentPath,
        modelName: selectedModel?.name || 'none',
        trustEnabled,
      }),
    [currentPath, messages.length, selectedModel?.name, trustEnabled],
  );

  const terminalCommandSummary = useMemo(() => {
    if (!availableTerminalCommands.length) {
      return 'No Terminal command catalog is attached.';
    }

    return availableTerminalCommands.join(', ');
  }, [availableTerminalCommands]);

  const terminalProgramSummary = useMemo(() => {
    if (!availablePrograms.length) {
      return 'No MapleOS app catalog is attached.';
    }

    return availablePrograms
      .map((program) => `${program.id} (${program.label})`)
      .join(', ');
  }, [availablePrograms]);

  const mapleOsStyleSummary = useMemo(() => {
    const themeMode = displaySettings.isDarkMode ? 'dark' : 'light';
    const accent = String(displaySettings.selectedAccent || 'red');
    const desktopColor = String(displaySettings.desktopColor || '200 25% 92%');
    const accentTitlebar = displaySettings.useAccentTitlebar === false ? 'off' : 'on';
    const primaryForegroundMode = String(displaySettings.primaryForegroundMode || 'auto');
    const accentForegroundMode = String(displaySettings.accentForegroundMode || 'auto');

    return [
      `theme=${themeMode}`,
      `accent=${accent}`,
      `desktopColor=${desktopColor}`,
      `accentTitlebar=${accentTitlebar}`,
      `primaryText=${primaryForegroundMode}`,
      `accentText=${accentForegroundMode}`,
    ].join('; ');
  }, [displaySettings]);

  const thinkingFrame = useMemo(
    () => buildThinkingFrame('MapleCode is thinking…', thinkingTick),
    [thinkingTick],
  );

  useEffect(() => {
    if (!models.length) {
      fetchModels().catch(() => undefined);
    }
  }, [fetchModels, models.length]);

  useEffect(() => {
    if (!selectedModelId && activeModels.length > 0) {
      setSelectedModelId(activeModels[0].id);
    }
  }, [activeModels, selectedModelId]);

  useEffect(() => {
    const trimmedInitialPrompt = initialPrompt?.trim();
    if (!trimmedInitialPrompt || initialPromptSubmittedRef.current || isLoading) {
      return;
    }

    initialPromptSubmittedRef.current = true;
    setInput(trimmedInitialPrompt);
  }, [initialPrompt, isLoading]);

  useEffect(() => {
    const trimmedInitialPrompt = initialPrompt?.trim();
    if (!trimmedInitialPrompt || !initialPromptSubmittedRef.current || isLoading || input !== trimmedInitialPrompt) {
      return;
    }

    void submitPrompt();
  }, [initialPrompt, input, isLoading]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      scrollViewportRef.current?.scrollIntoView({ block: 'end' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isTerminalPaneOpen || !terminalPaneEntries.length) return;

    const frame = window.requestAnimationFrame(() => {
      terminalPaneEndRef.current?.scrollIntoView({ block: 'end' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isTerminalPaneOpen, terminalPaneEntries]);

  useEffect(() => {
    if (!isLoading) return;
    setThinkingTick(0);
    const interval = window.setInterval(() => {
      setThinkingTick((current) => current + 1);
    }, SHIMMER_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'c') {
        event.preventDefault();
        onExit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onExit]);

  useEffect(() => {
    try {
      localStorage.setItem(INPUT_HISTORY_KEY, JSON.stringify(inputHistory));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [inputHistory]);

  const replaceMessages = (nextMessages: MapleCodeMessage[]) => {
    messagesRef.current = nextMessages;
    setMessages(nextMessages);
  };

  const appendMessage = (message: MapleCodeMessage) => {
    replaceMessages([...messagesRef.current, message]);
  };

  const appendTerminalPaneEntry = (kind: TerminalPaneEntry['kind'], content: string) => {
    setIsTerminalPaneOpen(true);
    setTerminalPaneEntries((current) => [
      ...current,
      {
        id: createId(),
        kind,
        content: content || '<no output>',
      },
    ]);
  };

  const runTerminalCommandWithPane = async (command: string) => {
    if (!runTerminalCommand) {
      return { kind: 'output', text: 'MapleCode cannot execute MapleOS Terminal commands in this session.' } as const;
    }

    appendTerminalPaneEntry('command', `terminal> ${command}`);
    const result = await runTerminalCommand(command);

    if (result.kind === 'input') {
      appendTerminalPaneEntry('input', result.prompt);
      return result;
    }

    appendTerminalPaneEntry('runtime', result.text || '<no output>');
    return result;
  };

  const submitTerminalInputWithPane = async (value: string) => {
    if (!submitTerminalInput) {
      return {
        kind: 'output',
        text: 'MapleCode cannot resume terminal input in this session because the Terminal input bridge is unavailable.',
      } as const;
    }

    appendTerminalPaneEntry('input', `input> ${value}`);
    const result = await submitTerminalInput(value);

    if (result.kind === 'input') {
      appendTerminalPaneEntry('input', result.prompt);
      return result;
    }

    appendTerminalPaneEntry('runtime', result.text || '<no output>');
    return result;
  };

  const clearTerminalPane = () => {
    setTerminalPaneEntries([]);
    setIsTerminalPaneOpen(false);
  };

  const recordInputHistory = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    setInputHistory((current) => {
      const withoutDuplicate = current.filter((entry) => entry !== trimmed);
      return [...withoutDuplicate, trimmed].slice(-MAX_INPUT_HISTORY);
    });
    setHistoryIndex(-1);
    historyDraftRef.current = '';
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void submitPrompt();
      return;
    }

    if (!inputHistory.length) return;

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (historyIndex === -1) {
        historyDraftRef.current = input;
      }

      const nextIndex = historyIndex === -1 ? inputHistory.length - 1 : Math.max(0, historyIndex - 1);
      setHistoryIndex(nextIndex);
      setInput(inputHistory[nextIndex] || '');
      return;
    }

    if (event.key === 'ArrowDown' && historyIndex !== -1) {
      event.preventDefault();

      if (historyIndex >= inputHistory.length - 1) {
        setHistoryIndex(-1);
        setInput(historyDraftRef.current);
        return;
      }

      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setInput(inputHistory[nextIndex] || '');
    }
  };

  const resolvePath = async (value?: string) => {
    const candidate = (value || '').trim();
    if (!candidate) return normalizeWindowsPath(currentPath);

    if (candidate === '..') {
      const normalized = normalizeWindowsPath(currentPath);
      if (normalized === 'C:\\') return normalized;

      const parts = normalized.split('\\').filter(Boolean);
      if (parts.length <= 1) return 'C:\\';
      const parent = parts.slice(0, -1).join('\\');
      return /^[A-Z]:$/i.test(parent) ? `${parent}\\` : parent;
    }

    if (/^[a-z]:\\/i.test(candidate) || /^[a-z]:$/i.test(candidate)) {
      return normalizeWindowsPath(candidate);
    }

    return joinWindowsPath(currentPath, candidate);
  };

  const syncNavigator = async () => {
    if (!navigator) return;
    await navigator.waitForInitialization();
    await navigator.syncWithVFS();
  };

  const ensureModuleFilesExist = async (
    rootPath: string,
    manifestPath: string,
    buildPath: string,
    watPath: string,
    wasmPath: string,
    manifest: string,
    watSource: string,
    wasmBase64: string,
  ) => {
    await createUserFile('C:\\Projects\\MapleCode Examples', '', true);
    await createUserFile(rootPath, '', true);
    await createUserFile(buildPath, '', true);
    await createUserFile(manifestPath, manifest, false, 'application/json');
    await createUserFile(watPath, watSource, false, 'text/plain');
    await createUserFile(wasmPath, wasmBase64, false, 'application/wasm');

    if (navigator) {
      await navigator.createFolder('C:\\Projects\\MapleCode Examples');
      await navigator.createFolder(rootPath);
      await navigator.createFolder(buildPath);
      await navigator.createFile(manifestPath, manifest);
      await navigator.createFile(watPath, watSource);
      await navigator.createFile(wasmPath, wasmBase64);
      await syncNavigator();
    }
  };

  const ensureDesktopAppFilesExist = async (
    rootPath: string,
    srcPath: string,
    manifestPath: string,
    htmlPath: string,
    readmePath: string,
    manifest: string,
    htmlSource: string,
    readmeSource: string,
  ) => {
    await createUserFile('C:\\Projects\\MapleCode Apps', '', true);
    await createUserFile(rootPath, '', true);
    await createUserFile(srcPath, '', true);
    await createUserFile(manifestPath, manifest, false, 'application/json');
    await createUserFile(htmlPath, htmlSource, false, 'text/html');
    await createUserFile(readmePath, readmeSource, false, 'text/markdown');

    if (navigator) {
      await navigator.createFolder('C:\\Projects\\MapleCode Apps');
      await navigator.createFolder(rootPath);
      await navigator.createFolder(srcPath);
      await navigator.createFile(manifestPath, manifest);
      await navigator.createFile(htmlPath, htmlSource);
      await navigator.createFile(readmePath, readmeSource);
      await syncNavigator();
    }
  };

  const sanitizeWasmModuleName = (value: string) => {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);

    return normalized || 'latest';
  };

  const sanitizeAppModuleName = (value: string) => {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9._-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);

    return normalized || 'latest-app';
  };

  const resolveSuggestedWasmName = (messages: MapleCodeMessage[]) => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      const suggestion = extractLatestWasmNameBlockFromText(message.content, message.label || message.role);
      if (suggestion?.name) {
        return sanitizeWasmModuleName(suggestion.name);
      }
    }

    return null;
  };

  const resolveSuggestedAppName = (messages: MapleCodeMessage[]) => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      const suggestion = extractLatestAppNameBlockFromText(message.content, message.label || message.role);
      if (suggestion?.name) {
        return sanitizeAppModuleName(suggestion.name);
      }
    }

    return null;
  };

  const buildNamedWasmPaths = (requestedName: string) => {
    const safeName = sanitizeWasmModuleName(requestedName);
    const displayName = requestedName.trim() || safeName;
    const rootPath = `C:\\Projects\\MapleCode Examples\\${safeName}`;
    const buildPath = `${rootPath}\\build`;

    return {
      safeName,
      displayName,
      rootPath,
      buildPath,
      manifestPath: `${rootPath}\\manifest.json`,
      watPath: `${buildPath}\\module.wat`,
      wasmPath: `${buildPath}\\module.wasm`,
    };
  };

  const buildNamedDesktopAppPaths = (requestedName: string) => {
    const safeName = sanitizeAppModuleName(requestedName);
    const displayName = requestedName.trim() || safeName;
    const rootPath = `C:\\Projects\\MapleCode Apps\\${safeName}`;
    const srcPath = `${rootPath}\\src`;

    return {
      safeName,
      displayName,
      rootPath,
      srcPath,
      manifestPath: `${rootPath}\\manifest.json`,
      htmlPath: `${srcPath}\\index.html`,
      readmePath: `${rootPath}\\README.md`,
    };
  };

  const decodeBase64 = (b64: string) => {
    const clean = b64.startsWith('data:') ? b64.split(',')[1] ?? '' : b64.trim();
    const bin = atob(clean);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let index = 0; index < len; index += 1) {
      bytes[index] = bin.charCodeAt(index);
    }
    return bytes;
  };

  const listDirectory = async (target?: string) => {
    if (!navigator) {
      return 'MapleCode file tools are unavailable because the MapleOS file system is not attached.';
    }

    await syncNavigator();
    const resolvedPath = await resolvePath(target);
    const item = navigator.findItemByPath(resolvedPath);

    if (!item) {
      return `Path not found: ${resolvedPath}`;
    }

    if (item.type === 'file') {
      return [`${resolvedPath}`, formatItemLine(item)].join('\n');
    }

    const children = navigator.getItemsInPath(resolvedPath);
    if (!children.length) {
      return `${resolvedPath}\n\n<empty directory>`;
    }

    return [resolvedPath, '', ...children.map(formatItemLine)].join('\n');
  };

  const readFileContent = async (target?: string) => {
    if (!navigator) {
      return 'MapleCode file tools are unavailable because the MapleOS file system is not attached.';
    }

    if (!target) {
      return 'Usage: /read <path>';
    }

    await syncNavigator();
    const resolvedPath = await resolvePath(target);
    const item = navigator.findItemByPath(resolvedPath);

    if (!item) {
      return `File not found: ${resolvedPath}`;
    }

    if (item.type !== 'file') {
      return `${resolvedPath} is a directory. Use /ls to inspect it.`;
    }

    return [`# ${resolvedPath}`, '', item.content || '<empty file>'].join('\n');
  };

  const changeDirectory = async (target?: string) => {
    if (!navigator) {
      return 'MapleCode file tools are unavailable because the MapleOS file system is not attached.';
    }

    if (!target) {
      return `Current directory: ${currentPath}`;
    }

    await syncNavigator();
    const resolvedPath = await resolvePath(target);
    const item = navigator.findItemByPath(resolvedPath);

    if (!item || item.type !== 'folder') {
      return `Directory not found: ${resolvedPath}`;
    }

    setCurrentPath(resolvedPath);
    return `Working directory changed to ${resolvedPath}`;
  };

  const setTrustMode = (enabled: boolean) => {
    const persisted = persistBoolean(TRUST_DEFAULT_KEY, enabled);
    if (!persisted) {
      return 'Unable to update Trust Mode in local storage.';
    }

    setTrustEnabled(enabled);
    return enabled
      ? 'Trust Mode enabled.'
      : 'Trust Mode disabled. EHCO Trust rules are no longer enforced by default.';
  };

  const setBypassMode = (enabled: boolean) => {
    const persisted = persistBoolean(BYPASS_DEFAULT_KEY, enabled);
    if (!persisted) {
      return 'Unable to update Bypass Permissions mode in local storage.';
    }

    setBypassEnabled(enabled);
    return enabled
      ? 'Bypass Permissions enabled. MapleCode can now auto-execute any MapleOS Terminal command exposed by the terminal bridge.'
      : 'Bypass Permissions disabled.';
  };

  const setMapleOsStyleMode = (enabled: boolean) => {
    const persisted = persistBoolean(MAPLEOS_STYLE_DEFAULT_KEY, enabled);
    if (!persisted) {
      return 'Unable to update MapleOS style mode in local storage.';
    }

    setMapleOsStyleEnabled(enabled);
    return enabled
      ? `MapleOS style mode enabled. MapleCode will use current display settings for desktop app generation.\n${mapleOsStyleSummary}`
      : 'MapleOS style mode disabled. MapleCode will stop biasing desktop apps toward the current MapleOS display settings.';
  };

  const listTerminalCommands = () => {
    if (!availableTerminalCommands.length) {
      return 'MapleCode does not have a Terminal command catalog in this session.';
    }

    return [
      'MapleOS Terminal commands:',
      '',
      ...availableTerminalCommands.map((command) => `- ${command}`),
    ].join('\n');
  };

  const listLaunchablePrograms = () => {
    if (!availablePrograms.length) {
      return 'MapleCode does not have a MapleOS app catalog in this session.';
    }

    return [
      'MapleOS command-line launchable apps:',
      '',
      ...availablePrograms.map((program) => `- ${program.id} — ${program.label}${program.description ? `: ${program.description}` : ''}`),
    ].join('\n');
  };

  const runMapleTerminalCommand = async (command: string) => {
    if (!runTerminalCommand) {
      return 'MapleCode cannot execute MapleOS Terminal commands in this session.';
    }

    const trimmed = command.trim();
    if (!trimmed) {
      return 'Usage: /cmd <terminal command>';
    }

    const result = await runTerminalCommandWithPane(trimmed);
    if (result.kind === 'input') {
      pendingTerminalInputShouldResumeAssistantRef.current = false;
      setPendingTerminalInputPrompt(result.prompt);
      return [
        'Terminal command is waiting for user input.',
        result.prompt,
      ].join('\n');
    }

    return result.text;
  };

  const instantiateWasmSilently = async (bytes: Uint8Array) => {
    if (navigator) {
      await syncNavigator();
    }

    const MAPLE_OK = 0;
    const MAPLE_ERR_NOT_FOUND = 1;
    const MAPLE_ERR_INVALID_ARG = 2;
    const MAPLE_ERR_ALREADY_EXISTS = 3;
    const MAPLE_ERR_BUFFER_TOO_SMALL = 4;
    const MAPLE_ERR_IO = 5;
    const MAPLE_ERR_BAD_HANDLE = 6;

    let exportedMemory: WebAssembly.Memory | null = null;
    const runtimeLines: string[] = [];
    const decoder = new TextDecoder();
    const encoder = new TextEncoder();
    const directoryHandles = new Map<number, { entries: FileSystemItem[]; index: number }>();
    let nextDirectoryHandle = 1;
    let scratchPointer = 48 * 1024;

    const ensureMemoryCapacity = (requiredEnd: number) => {
      if (!exportedMemory) {
        return false;
      }

      while (requiredEnd > exportedMemory.buffer.byteLength) {
        exportedMemory.grow(Math.ceil((requiredEnd - exportedMemory.buffer.byteLength) / 65536));
      }

      return true;
    };

    const writeI32 = (pointer: number, value: number) => {
      if (!exportedMemory) {
        return false;
      }

      const start = pointer >>> 0;
      if (!ensureMemoryCapacity(start + 4)) {
        return false;
      }

      new DataView(exportedMemory.buffer).setInt32(start, value | 0, true);
      return true;
    };

    const readUtf8 = (pointer: number, length: number) => {
      if (!exportedMemory) {
        return '[host log failed: memory export not found]';
      }

      const start = pointer >>> 0;
      const size = length >>> 0;
      const bytesView = new Uint8Array(exportedMemory.buffer);
      const end = start + size;

      if (end > bytesView.byteLength) {
        return `[host log failed: invalid UTF-8 range ${start}-${end}]`;
      }

      return decoder.decode(bytesView.subarray(start, end));
    };

    const readCString = (pointer: number, maxLength = 4096) => {
      if (!exportedMemory) {
        return '';
      }

      const start = pointer >>> 0;
      const bytesView = new Uint8Array(exportedMemory.buffer);
      if (start >= bytesView.byteLength) {
        return '';
      }

      let end = start;
      const limit = Math.min(bytesView.byteLength, start + maxLength);
      while (end < limit && bytesView[end] !== 0) {
        end += 1;
      }

      return decoder.decode(bytesView.subarray(start, end));
    };

    const writeUtf8Buffer = (
      outPointer: number,
      outCapacity: number,
      outLengthPointer: number,
      value: string,
      nullTerminate = false,
    ) => {
      if (!exportedMemory) {
        return MAPLE_ERR_IO;
      }

      const targetPointer = outPointer >>> 0;
      const targetCapacity = outCapacity >>> 0;
      const encoded = encoder.encode(value);
      const required = encoded.length + (nullTerminate ? 1 : 0);

      if (outLengthPointer) {
        writeI32(outLengthPointer, encoded.length);
      }

      if (targetCapacity < required) {
        return MAPLE_ERR_BUFFER_TOO_SMALL;
      }

      if (!ensureMemoryCapacity(targetPointer + required)) {
        return MAPLE_ERR_IO;
      }

      const bytesView = new Uint8Array(exportedMemory.buffer);
      bytesView.set(encoded, targetPointer);
      if (nullTerminate) {
        bytesView[targetPointer + encoded.length] = 0;
      }

      return MAPLE_OK;
    };

    const allocateCString = (value: string) => {
      if (!exportedMemory) {
        return 0;
      }

      const encoded = encoder.encode(value);
      const allocationSize = Math.max(encoded.length + 1, 256);
      if (!ensureMemoryCapacity(scratchPointer + allocationSize)) {
        return 0;
      }

      const ptr = scratchPointer;
      const bytesView = new Uint8Array(exportedMemory.buffer);
      bytesView.set(encoded, ptr);
      bytesView[ptr + encoded.length] = 0;
      scratchPointer += allocationSize;
      return ptr;
    };

    const resolveImportPath = (value: string) => {
      const candidate = value.trim();
      if (!candidate) {
        return normalizeWindowsPath(currentPath);
      }

      if (/^[a-z]:\\/i.test(candidate) || /^[a-z]:$/i.test(candidate)) {
        return normalizeWindowsPath(candidate);
      }

      return joinWindowsPath(currentPath, candidate);
    };

    const listDirectoryEntriesForImport = (path: string) => {
      if (!navigator) {
        return null;
      }

      const resolvedPath = resolveImportPath(path);
      const item = navigator.findItemByPath(resolvedPath);
      if (!item || item.type !== 'folder') {
        return null;
      }

      return navigator.getItemsInPath(resolvedPath);
    };

    const createDirectoryForImport = (fullPath: string) => {
      const targetPath = resolveImportPath(fullPath);
      if (!targetPath) {
        return MAPLE_ERR_INVALID_ARG;
      }

      if (navigator?.findItemByPath(targetPath)) {
        return MAPLE_ERR_ALREADY_EXISTS;
      }

      try {
        if (navigator) {
          void navigator.createFolder(targetPath);
        }
        void createUserFile(targetPath, '', true);
        return MAPLE_OK;
      } catch {
        return MAPLE_ERR_IO;
      }
    };

    const logRuntimeLine = (message: string) => {
      runtimeLines.push(message);
    };

    const isMapleExitSignal = (value: unknown): value is { __mapleExit: true; code: number } => {
      return typeof value === 'object'
        && value !== null
        && (value as { __mapleExit?: boolean }).__mapleExit === true;
    };

    const sharedImports = {
      abort() {
        logRuntimeLine('WASM abort');
      },
      log_utf8(pointer: number, length: number) {
        logRuntimeLine(readUtf8(pointer, length));
      },
      print_utf8(pointer: number, length: number) {
        logRuntimeLine(readUtf8(pointer, length));
      },
      printf(pointer: number, length: number) {
        logRuntimeLine(readUtf8(pointer, length));
      },
      log_i32(value: number) {
        logRuntimeLine(String(value | 0));
      },
      strlen(pointer: number) {
        return encoder.encode(readCString(pointer)).length;
      },
      dir_open(pathPointer: number) {
        const entries = listDirectoryEntriesForImport(readCString(pathPointer));
        if (!entries) {
          return 0;
        }

        const handle = nextDirectoryHandle;
        nextDirectoryHandle += 1;
        directoryHandles.set(handle, { entries, index: 0 });
        return handle;
      },
      dir_read(handle: number) {
        const directory = directoryHandles.get(handle | 0);
        if (!directory) {
          return 0;
        }

        const nextEntry = directory.entries[directory.index];
        if (!nextEntry) {
          return 0;
        }

        directory.index += 1;
        return allocateCString(nextEntry.name);
      },
      dir_close(handle: number) {
        directoryHandles.delete(handle | 0);
        return MAPLE_OK;
      },
      mkdir(parentPathPointer: number, namePointer: number) {
        const parentPath = resolveImportPath(readCString(parentPathPointer));
        const rawName = readCString(namePointer).trim();
        if (!rawName) {
          return MAPLE_ERR_INVALID_ARG;
        }

        const targetPath = /^[a-z]:\\/i.test(rawName) || /^[a-z]:$/i.test(rawName)
          ? normalizeWindowsPath(rawName)
          : joinWindowsPath(parentPath, rawName);

        return createDirectoryForImport(targetPath);
      },
      maple_log(pointer: number, length: number) {
        logRuntimeLine(readUtf8(pointer, length));
      },
      maple_error(pointer: number, length: number) {
        logRuntimeLine(`ERROR: ${readUtf8(pointer, length)}`);
      },
      maple_exit(code: number) {
        throw { __mapleExit: true, code: code | 0 };
      },
      maple_strlen(pointer: number) {
        return encoder.encode(readCString(pointer)).length;
      },
      maple_getcwd(outPointer: number, outCapacity: number, outLengthPointer: number) {
        return writeUtf8Buffer(outPointer, outCapacity, outLengthPointer, normalizeWindowsPath(currentPath), false);
      },
      maple_dir_open(pathPointer: number) {
        const entries = listDirectoryEntriesForImport(readCString(pathPointer));
        if (!entries) {
          return 0;
        }

        const handle = nextDirectoryHandle;
        nextDirectoryHandle += 1;
        directoryHandles.set(handle, { entries, index: 0 });
        return handle;
      },
      maple_dir_read(handle: number, outPointer: number, outCapacity: number, outLengthPointer: number, outKindPointer: number) {
        const directory = directoryHandles.get(handle | 0);
        if (!directory) {
          return MAPLE_ERR_BAD_HANDLE;
        }

        const nextEntry = directory.entries[directory.index];
        if (!nextEntry) {
          return MAPLE_ERR_NOT_FOUND;
        }

        const writeStatus = writeUtf8Buffer(outPointer, outCapacity, outLengthPointer, nextEntry.name, false);
        if (writeStatus !== MAPLE_OK) {
          return writeStatus;
        }

        if (outKindPointer && !writeI32(outKindPointer, nextEntry.type === 'folder' ? 2 : 1)) {
          return MAPLE_ERR_IO;
        }

        directory.index += 1;
        return MAPLE_OK;
      },
      maple_dir_close(handle: number) {
        if (!directoryHandles.has(handle | 0)) {
          return MAPLE_ERR_BAD_HANDLE;
        }

        directoryHandles.delete(handle | 0);
        return MAPLE_OK;
      },
      maple_mkdir(pathPointer: number) {
        const fullPath = readCString(pathPointer).trim();
        if (!fullPath) {
          return MAPLE_ERR_INVALID_ARG;
        }

        return createDirectoryForImport(fullPath);
      },
    };

    const imports: WebAssembly.Imports = {
      env: sharedImports,
      host: sharedImports,
    };

    const result = (await WebAssembly.instantiate(bytes, imports)) as WebAssembly.Instance | WebAssembly.WebAssemblyInstantiatedSource;
    const exportsObject = ('instance' in result ? result.instance.exports : result.exports) as Record<string, any>;
    exportedMemory = Object.values(exportsObject).find((value) => value instanceof WebAssembly.Memory) ?? null;
    const exportNames = Object.keys(exportsObject);

    runtimeLines.unshift(`Exports: ${exportNames.length > 0 ? exportNames.join(', ') : 'none'}`);

    const tryAutoRun = (exportName: '_start' | 'main' | 'hello') => {
      const candidate = exportsObject[exportName];
      if (typeof candidate !== 'function' || candidate.length > 0) {
        return false;
      }

      try {
        const resultValue = candidate();
        if (resultValue !== undefined) {
          logRuntimeLine(`${exportName}() => ${String(resultValue)}`);
        } else {
          logRuntimeLine(`Auto-ran ${exportName}()`);
        }
      } catch (error) {
        if (isMapleExitSignal(error)) {
          logRuntimeLine(`Process exited with code ${String(error.code)}`);
          return true;
        }
        throw error;
      }

      return true;
    };

    if (!tryAutoRun('_start') && !tryAutoRun('main') && !tryAutoRun('hello')) {
      if (typeof exportsObject.add === 'function') {
        const addResult = exportsObject.add(1, 2);
        logRuntimeLine(`add(1,2) = ${String(addResult)}`);
      } else {
        logRuntimeLine('No auto-run entry found. Export _start(), main(), or hello() to run code automatically.');
        logRuntimeLine('Available MapleOS ABI v1 imports: env.maple_log(ptr,len), env.maple_error(ptr,len), env.maple_exit(code), env.maple_strlen(cstr_ptr), env.maple_getcwd(out_ptr,out_cap,out_len_ptr), env.maple_dir_open(path_ptr), env.maple_dir_read(handle,out_ptr,out_cap,out_len_ptr,out_kind_ptr), env.maple_dir_close(handle), env.maple_mkdir(path_ptr).');
      }
    }

    return runtimeLines;
  };

  const runWatExampleSilently = async (
    watBlock: { code: string; label: string },
    moduleName: string,
    mode: 'auto' | 'manual' = 'manual',
  ) => {
    try {
      const wabtFactory = (await import('wabt')).default as any;
      const wabtInstance = await wabtFactory();
      const parsed = wabtInstance.parseWat('module.wat', watBlock.code);
      const binary = parsed.toBinary({ log: false });
      const bytes = new Uint8Array(binary.buffer);
      const wasmBase64 = bytesToBase64(bytes);
      const paths = buildNamedWasmPaths(moduleName);

      const manifest = JSON.stringify(
        {
          name: paths.displayName,
          version: '0.1.0',
          description: 'Temporary MapleCode WAT example',
          author: 'MapleCode',
          categories: ['developer', 'wasm'],
          permissions: ['windows:open'],
          aiCapability: false,
          mapleOSVersion: '>=1.0.0',
          type: 'wasm',
          entry: { wasm: 'build/module.wasm' },
        },
        null,
        2,
      );

      await ensureModuleFilesExist(
        paths.rootPath,
        paths.manifestPath,
        paths.buildPath,
        paths.watPath,
        paths.wasmPath,
        manifest,
        watBlock.code,
        wasmBase64,
      );

      const summaryLines = [
        `${mode === 'auto' ? 'Auto-detected' : 'Extracted'} WAT block from ${watBlock.label}.`,
        `Module name: ${paths.displayName}`,
        `Compiled to WASM successfully (${bytes.byteLength} bytes).`,
        `Saved temp module to: ${paths.rootPath}`,
        `Manifest: ${paths.manifestPath}`,
        `Source WAT: ${paths.watPath}`,
        `Compiled WASM: ${paths.wasmPath}`,
        'Running silently in MapleCode output…',
      ];

      appendTerminalPaneEntry('compile', summaryLines.join('\n'));

      const runtimeLines = await instantiateWasmSilently(bytes);
      appendTerminalPaneEntry('runtime', runtimeLines.join('\n') || '<no output>');

      return [...summaryLines, '', ...runtimeLines].join('\n');
    } catch (error) {
      const lines = [
        `${mode === 'auto' ? 'Auto-compile' : 'Compilation'} failed for the WAT example.`,
        `Module name: ${moduleName.trim() || 'latest'}`,
        error instanceof Error ? error.message : 'Unknown WAT compilation error.',
      ];
      appendTerminalPaneEntry('compile', lines.join('\n'));
      return lines.join('\n');
    }
  };

  const runSavedNamedWasmSilently = async (moduleName: string) => {
    const paths = buildNamedWasmPaths(moduleName);
    const watFile = (await reloadUserFile(paths.watPath)) ?? getUserFile(paths.watPath);
    const wasmFile = (await reloadUserFile(paths.wasmPath)) ?? getUserFile(paths.wasmPath);
    const manifestFile = (await reloadUserFile(paths.manifestPath)) ?? getUserFile(paths.manifestPath);

    if (!watFile?.content?.trim() && !wasmFile?.content?.trim()) {
      return [
        `No saved module named "${moduleName}" was found.`,
        'Generate a ```wat block first, then run /run <wasmname> to save and test it.',
      ].join('\n');
    }

    try {
      let bytes: Uint8Array;
      const summaryLines = [
        `Running saved module: ${paths.displayName}`,
        `Project path: ${paths.rootPath}`,
        manifestFile?.content ? `Manifest: ${paths.manifestPath}` : 'Manifest: <not found>',
      ];

      if (watFile?.content?.trim()) {
        const wabtFactory = (await import('wabt')).default as any;
        const wabtInstance = await wabtFactory();
        const parsed = wabtInstance.parseWat('module.wat', watFile.content);
        const binary = parsed.toBinary({ log: false });
        bytes = new Uint8Array(binary.buffer);
        summaryLines.push(`Source WAT: ${paths.watPath}`);
        summaryLines.push(`Compiled WASM: ${paths.wasmPath}`);
        summaryLines.push(`Compiled to WASM successfully (${bytes.byteLength} bytes).`);
      } else {
        bytes = decodeBase64(wasmFile!.content);
        summaryLines.push(`Compiled WASM: ${paths.wasmPath}`);
        summaryLines.push(`Loaded WASM successfully (${bytes.byteLength} bytes).`);
      }

      summaryLines.push('Running silently in MapleCode output…');
      appendTerminalPaneEntry('compile', summaryLines.join('\n'));

      const runtimeLines = await instantiateWasmSilently(bytes);
      appendTerminalPaneEntry('runtime', runtimeLines.join('\n') || '<no output>');

      return [...summaryLines, '', ...runtimeLines].join('\n');
    } catch (error) {
      const lines = [
        `Failed to run saved module: ${paths.displayName}`,
        error instanceof Error ? error.message : 'Unknown WASM runtime error.',
      ];
      appendTerminalPaneEntry('compile', lines.join('\n'));
      return lines.join('\n');
    }
  };

  const runNamedWatModule = async (moduleName?: string) => {
    const explicitName = (moduleName || '').trim();
    const resolvedName = explicitName || resolveSuggestedWasmName(messagesRef.current) || 'latest';

    const latestWat = extractLatestWatBlock(messagesRef.current);
    if (latestWat) {
      return runWatExampleSilently({ code: latestWat.code, label: latestWat.label }, resolvedName, 'manual');
    }

    if (!explicitName && resolvedName === 'latest') {
      return [
        'Usage: /run [wasmname]',
        'Tip: ask MapleCode for a WAT example first so it can suggest a name such as ```wasmname helloworld```.',
      ].join('\n');
    }

    return runSavedNamedWasmSilently(resolvedName);
  };

  const runLatestWatExample = async () => runNamedWatModule('latest');

  const runWatExampleFromBlock = async (
    watBlock: { code: string; label: string },
    mode: 'auto' | 'manual' = 'manual',
  ) => {
    const suggestedName = resolveSuggestedWasmName(messagesRef.current) || 'latest';
    return runWatExampleSilently(watBlock, suggestedName, mode);
  };

  const saveDesktopAppSilently = async (
    htmlBlock: { html: string; label: string },
    moduleName: string,
    mode: 'auto' | 'manual' = 'manual',
  ) => {
    const paths = buildNamedDesktopAppPaths(moduleName);
    console.log('[MapleCodeREPL] saveDesktopAppSilently:start', {
      moduleName,
      mode,
      label: htmlBlock.label,
      rootPath: paths.rootPath,
      hasOnOpenWindow: Boolean(onOpenWindow),
      htmlLength: htmlBlock.html.length,
    });
    const manifest = JSON.stringify(
      {
        name: paths.displayName,
        version: '0.1.0',
        description: 'MapleCode desktop UI app',
        author: 'MapleCode',
        categories: ['desktop', 'ui'],
        permissions: ['windows:open', 'notifications:show', 'files:read', 'files:write'],
        aiCapability: false,
        mapleOSVersion: '>=1.0.0',
        type: 'web',
        entry: { html: 'src/index.html' },
      },
      null,
      2,
    );
    const readme = [
      `# ${paths.displayName}`,
      '',
      'Generated by MapleCode as a MapleOS desktop UI app.',
      '',
      '- Manifest: manifest.json',
      '- Entry HTML: src/index.html',
      '- Preview: open in Module Host',
    ].join('\n');

    await ensureDesktopAppFilesExist(
      paths.rootPath,
      paths.srcPath,
      paths.manifestPath,
      paths.htmlPath,
      paths.readmePath,
      manifest,
      htmlBlock.html,
      readme,
    );

    const summaryLines = [
      `${mode === 'auto' ? 'Auto-detected' : 'Extracted'} desktop app HTML block from ${htmlBlock.label}.`,
      `App name: ${paths.displayName}`,
      `Saved desktop app to: ${paths.rootPath}`,
      `Manifest: ${paths.manifestPath}`,
      `Entry HTML: ${paths.htmlPath}`,
    ];

    appendTerminalPaneEntry('compile', summaryLines.join('\n'));

    const runtimeLines = onOpenWindow
      ? [`Opened ${paths.displayName} in a standalone MapleOS app window.`]
      : ['Desktop app saved, but window launching is unavailable in this session.'];

    if (onOpenWindow) {
      console.log('[MapleCodeREPL] saveDesktopAppSilently:openWindow', {
        appId: 'web-app-runner',
        title: paths.displayName,
        rootPath: paths.rootPath,
      });
      onOpenWindow('web-app-runner', paths.displayName, paths.rootPath);
    }

    appendTerminalPaneEntry('runtime', runtimeLines.join('\n'));
    console.log('[MapleCodeREPL] saveDesktopAppSilently:done', {
      moduleName: paths.displayName,
      mode,
      openedWindow: Boolean(onOpenWindow),
    });
    return [...summaryLines, '', ...runtimeLines].join('\n');
  };

  const openNamedDesktopApp = async (moduleName?: string) => {
    const explicitName = (moduleName || '').trim();
    const resolvedName = explicitName || resolveSuggestedAppName(messagesRef.current) || 'latest-app';
    const latestHtml = extractLatestHtmlBlock(messagesRef.current);

    if (latestHtml) {
      return saveDesktopAppSilently(latestHtml, resolvedName, 'manual');
    }

    const paths = buildNamedDesktopAppPaths(resolvedName);
    const manifestFile = (await reloadUserFile(paths.manifestPath)) ?? getUserFile(paths.manifestPath);
    const htmlFile = (await reloadUserFile(paths.htmlPath)) ?? getUserFile(paths.htmlPath);

    if (!manifestFile?.content?.trim() || !htmlFile?.content?.trim()) {
      return [
        `No saved desktop app named "${resolvedName}" was found.`,
        'Ask MapleCode to generate a desktop app with ```appname and ```html blocks, then run /open-app [appname].',
      ].join('\n');
    }

    const summaryLines = [
      `Opening saved desktop app: ${paths.displayName}`,
      `Project path: ${paths.rootPath}`,
      `Manifest: ${paths.manifestPath}`,
      `Entry HTML: ${paths.htmlPath}`,
    ];

    appendTerminalPaneEntry('compile', summaryLines.join('\n'));

    const runtimeLines = onOpenWindow
      ? [`Opened ${paths.displayName} in a standalone MapleOS app window.`]
      : ['Desktop app found, but window launching is unavailable in this session.'];

    if (onOpenWindow) {
      onOpenWindow('web-app-runner', paths.displayName, paths.rootPath);
    }

    appendTerminalPaneEntry('runtime', runtimeLines.join('\n'));
    return [...summaryLines, '', ...runtimeLines].join('\n');
  };

  const recoverDesktopAppFromRecentMessages = async (startIndex: number) => {
    const recentMessages = messagesRef.current.slice(startIndex);
    const hasRecentAppTool = recentMessages.some((message) => message.role === 'tool' && message.label === 'app');
    const recentHtml = extractLatestHtmlBlock(recentMessages);
    console.log('[MapleCodeREPL] recoverDesktopAppFromRecentMessages:scan', {
      startIndex,
      recentMessageCount: recentMessages.length,
      hasRecentAppTool,
      hasRecentHtml: Boolean(recentHtml),
    });

    if (hasRecentAppTool) {
      return false;
    }

    if (!recentHtml) {
      return false;
    }

    const appName = resolveSuggestedAppName(recentMessages) || resolveSuggestedAppName(messagesRef.current) || 'latest-app';
    console.log('[MapleCodeREPL] recoverDesktopAppFromRecentMessages:launch', {
      appName,
      label: recentHtml.label,
    });
    const runResult = await saveDesktopAppSilently(recentHtml, appName, 'auto');
    appendMessage(createMessage('tool', runResult, 'app'));
    return true;
  };

  const runSlashCommand = async (rawInput: string) => {
    const trimmed = rawInput.trim();
    const [command, ...rest] = trimmed.replace(/^\//, '').split(/\s+/);
    const argument = rest.join(' ').trim();

    switch ((command || '').toLowerCase()) {
      case 'help':
        return { label: 'help', content: TOOL_SUMMARY.join('\n') };
      case 'status':
        return {
          label: 'status',
          content: [
            `Working directory: ${currentPath}`,
            `Selected model: ${selectedModel?.name || 'none'}`,
            `AI ready: ${selectedModel ? 'yes' : 'no'}`,
            `Trust Mode: ${trustEnabled ? 'enabled' : 'disabled'}`,
            `Bypass Permissions: ${bypassEnabled ? 'enabled' : 'disabled'}`,
            `MapleOS style mode: ${mapleOsStyleEnabled ? 'enabled' : 'disabled'}`,
            ...(mapleOsStyleEnabled ? [`MapleOS display settings: ${mapleOsStyleSummary}`] : []),
            `File tools: ${navigator ? 'attached' : 'unavailable'}`,
            `Terminal bridge: ${runTerminalCommand ? 'attached' : 'unavailable'}`,
            `Auto terminal loop: ${((trustEnabled || bypassEnabled) && runTerminalCommand) ? 'enabled' : 'disabled'}`,
          ].join('\n'),
        };
      case 'trust':
        return { label: 'trust', content: setTrustMode(argument.toLowerCase() !== 'off') };
      case 'bypass':
        return { label: 'bypass', content: setBypassMode(argument.toLowerCase() === 'on') };
      case 'os-style':
      case 'maple-style':
        return { label: 'os-style', content: setMapleOsStyleMode(argument.toLowerCase() !== 'off') };
      case 'pwd':
        return { label: 'pwd', content: `Current directory: ${currentPath}` };
      case 'ls':
      case 'dir':
        return { label: 'ls', content: await listDirectory(argument) };
      case 'cd':
        return { label: 'cd', content: await changeDirectory(argument) };
      case 'read':
      case 'cat':
      case 'type':
        return { label: 'read', content: await readFileContent(argument) };
      case 'commands':
        return { label: 'commands', content: listTerminalCommands() };
      case 'apps':
        return { label: 'apps', content: listLaunchablePrograms() };
      case 'cmd':
        return { label: 'cmd', content: await runMapleTerminalCommand(argument) };
      case 'run':
        return { label: 'run', content: await runNamedWatModule(argument) };
      case 'run-example':
      case 'wasm-run':
        return { label: 'run', content: await runLatestWatExample() };
      case 'open-app':
      case 'run-app':
      case 'preview-app':
        return { label: 'app', content: await openNamedDesktopApp(argument) };
      case 'tools':
        return { label: 'tools', content: TOOL_SUMMARY.join('\n') };
      case 'clear':
        setPendingInputPrompt(null);
        setPendingTerminalInputPrompt(null);
        pendingTerminalInputShouldResumeAssistantRef.current = false;
        pendingInputContinuationRef.current = null;
        clearTerminalPane();
        replaceMessages([
          createMessage('system', 'MapleCode transcript cleared.', 'session'),
        ]);
        return null;
      case 'exit':
        onExit();
        return null;
      default:
        return {
          label: 'command',
          content: `Unknown MapleCode command: ${command || trimmed}\n\nType /help to see the supported commands.`,
        };
    }
  };

  const buildModelMessages = (transcript: MapleCodeMessage[]): Message[] => {
    const systemPrompt: Message = {
      id: createId(),
      chatId: 'maplecode',
      content: [
        'You are MapleCode, a concise coding assistant running inside the MapleOS terminal.',
        `Current working directory: ${currentPath}`,
        `Trust Mode is currently ${trustEnabled ? 'enabled' : 'disabled'}.`,
        `Bypass Permissions is currently ${bypassEnabled ? 'enabled' : 'disabled'}.`,
        `MapleOS style mode is currently ${mapleOsStyleEnabled ? 'enabled' : 'disabled'}.`,
        'The user can access browser-safe tools through slash commands such as /ls, /cd, /read, /commands, /apps, /cmd, /run [wasmname], /open-app [appname], /os-style on, /trust off, and /bypass on.',
        'When the user needs filesystem output, tell them which slash command to run instead of inventing tool results.',
        'MapleCode is attached to the full MapleOS Terminal command bridge and app catalog for this session.',
        `Known Terminal commands: ${terminalCommandSummary}`,
        `Known command-line launchable apps: ${terminalProgramSummary}`,
        'If the user asks about command-line tools or MapleOS apps, use the attached command and app catalog instead of guessing.',
        'Terminal commands can request stdin-like user input through the MapleOS Terminal bridge. If a command or script asks for input, MapleCode will pause, ask the user, and resume the command after the reply.',
        bypassEnabled
          ? `Bypass Permissions is enabled. You may use exactly one fenced \`\`\`terminal block to request execution of up to ${MAX_AUTO_TERMINAL_STEPS} MapleOS Terminal commands or launchable apps, with one raw command per non-empty line.`
          : `If Trust Mode is enabled and you want MapleCode to execute MapleOS Terminal commands, emit exactly one fenced \`\`\`terminal block containing up to ${MAX_AUTO_TERMINAL_STEPS} raw command lines, with one command per non-empty line and nothing else inside that block.`,
        'Inside a fenced ```terminal block, never prefix lines with /cmd because MapleCode executes those lines directly through the terminal bridge.',
        'Use /cmd only when you are telling the user what to type manually outside a fenced ```terminal block.',
        'Only use ```terminal blocks for commands you intend MapleCode to execute. Use other fenced languages for examples that should not run.',
        'After MapleCode returns a [tool:cmd] result, continue from that result instead of repeating the same command.',
        !bypassEnabled && !trustEnabled
          ? 'Because neither Trust Mode nor Bypass Permissions is enabled, do not assume terminal blocks will be executed automatically; point the user to /cmd instead.'
          : 'The auto terminal loop is available in this session.',
        'Do not invent runtime input APIs such as env.input_utf8, host.input_utf8, stdin, scanf, read_line, or prompt(). They are not available in MapleCode workflows.',
        'If you need the user\'s name or any other value, first emit exactly one fenced ```input block, wait for the reply, and only then continue with code generation or terminal execution.',
        'If a previous attempt failed because of a missing runtime input function, recover by asking the user via a ```input block instead of proposing another unsupported import.',
        'If you need information from the user before continuing a workflow, emit exactly one fenced ```input block containing only the question or prompt you want MapleCode to ask the user.',
        'When you emit a ```input block, stop and wait for the user response before emitting any ```terminal block.',
        'Treat the user\'s next reply as the answer to the most recent ```input request.',
        'When MapleCode adds an [tool:input] message saying Input reply received, treat that value as resolved input and immediately continue the suspended task instead of only describing next steps.',
        'If the transcript also contains an older MapleCode runtime input reminder, ignore that reminder once a later Input reply received message is present and continue from the resolved answer.',
        mapleOsStyleEnabled
          ? `Current MapleOS display settings for desktop app styling: ${mapleOsStyleSummary}.`
          : 'Do not force MapleOS-native visual styling unless the user explicitly asks for it.',
        mapleOsStyleEnabled
          ? MAPLEOS_NATIVE_APP_STYLE_GUIDE
          : 'When using a generic desktop app style, still prefer clean, practical structure over decorative full-page styling.',
        mapleOsStyleEnabled
          ? 'When generating desktop UI apps, use the current MapleOS display settings as visual source of truth. Match the active dark/light mode, accent color, desktop tone, contrast, and titlebar behavior instead of default browser styling.'
          : 'When generating desktop UI apps, you may keep styling neutral unless the user requests a MapleOS-native look.',
        mapleOsStyleEnabled
          ? 'In MapleOS style mode, prefer polished MapleOS-like layouts: subtle borders, rounded panels, restrained shadows, accent highlights, strong contrast, and toolbar/header styling that fits the current MapleOS display settings.'
          : 'Keep desktop app styling practical and readable.',
        mapleOsStyleEnabled
          ? 'In MapleOS style mode, always make html and body theme-aware. Use MapleOS CSS variables such as hsl(var(--background)), hsl(var(--foreground)), hsl(var(--card)), hsl(var(--border)), hsl(var(--primary)), hsl(var(--primary-foreground)), hsl(var(--accent)), and hsl(var(--accent-foreground)) instead of hardcoded page colors.'
          : 'For desktop UI apps, avoid unnecessary hardcoded full-page colors when simple defaults work.',
        mapleOsStyleEnabled
          ? 'Do not hardcode a fixed light page background for the whole app. The app shell must react to MapleOS light/dark changes automatically.'
          : 'If you add custom styling, keep it readable in the intended theme.',
        'If that suspended task is a WAT/WASM workflow, continue by returning updated ```wasmname and ```wat blocks so MapleCode can finish the compile/run flow.',
        'If that suspended task is a desktop UI app workflow, continue by returning updated ```appname and ```html blocks so MapleCode can save and preview it.',
        'If you provide a WAT/WASM example, also emit exactly one fenced ```wasmname block containing a short stable lowercase app name with no spaces, such as helloworld for a hello-world example.',
        'If you provide a MapleOS desktop application with UI, also emit exactly one fenced ```appname block containing a short stable lowercase app name with no spaces, and one self-contained fenced ```html block suitable for src/index.html.',
        'For MapleOS desktop UI apps, keep CSS and JavaScript inline in the HTML unless the user explicitly asks for multiple files.',
        'MapleOS desktop UI apps running in Module Host now have a host bridge available as window.MapleOS and window.mapleOS.',
        'Desktop host API methods available in UI apps are: MapleOS.app.getInfo(), MapleOS.notifications.show({ title, message }), MapleOS.windows.open({ appId, title, filePath?, content? }), MapleOS.fs.readFile(path), MapleOS.fs.writeFile(path, content, { contentType? }), MapleOS.fs.mkdir(path), MapleOS.fs.list(path), and MapleOS.host.log(...parts).',
        'Desktop host API permissions come from the module manifest. MapleCode-generated desktop apps should assume windows:open, notifications:show, files:read, and files:write are available unless the user asks for a more restricted manifest.',
        'When writing desktop UI apps that use the MapleOS bridge, prefer async/await and call window.MapleOS APIs from button handlers or startup logic as needed.',
        'For MapleOS-native desktop apps, prefer inline controls, dialogs, forms, sidebars, and buttons over browser prompt() for core flows like naming, saving, or creating notes.',
        'If you use prompt() at all, treat it as a small fallback only, never as the main interaction model.',
        'If you generate a desktop notes app, editor, file browser, launcher, or dashboard, you may use MapleOS.fs.* to load and save text files and MapleOS.notifications.show() for user feedback.',
        'If you generate a launcher-style desktop app, you may use MapleOS.windows.open() to open another MapleOS app window.',
        'Prefer simple browser-safe HTML, CSS, and JavaScript that can run in a standalone MapleOS app window without external dependencies.',
        'Prefer descriptive names that match the feature, like helloworld, counterdemo, textprinter, mathdemo, notesapp, or taskboard.',
        'If the user asks you to build or revise a WAT/WASM example, prefer giving it a stable name and assume they usually want a one-shot code, compile, and run flow unless they explicitly say not to run it.',
        'If the user asks for a WAT example, always return a valid fenced ```wat block with no placeholders so MapleCode can compile it.',
        'If the user asks for a build/create/make/generate request and you choose to implement it as WAT/WASM, return runnable ```wasmname and ```wat blocks because MapleCode may compile and run them automatically.',
        'If the user asks for a MapleOS terminal application, treat that as a request for a WAT/WASM MapleOS app by default unless they explicitly ask for a script, batch file, or raw command sequence instead.',
        'If the user asks for a MapleOS terminal application, prefer runnable ```wasmname and ```wat blocks over manual /cmd instructions or fenced ```terminal blocks.',
        'If the user asks for a terminal app/program/tool and you choose to implement it as WAT/WASM, return runnable ```wasmname and ```wat blocks because MapleCode may compile and run them automatically.',
        'If the user asks for a MapleOS desktop application, desktop app, GUI app, or UI application, prefer runnable ```appname and ```html blocks over long prose.',
        'MapleCode may automatically save desktop UI app blocks to a MapleOS web app project and open them in a standalone MapleOS app window.',
        'Target MapleOS WASM ABI v1 by default for MapleOS terminal applications. Prefer env.maple_* imports over older generic imports.',
        'MapleOS WASM ABI v1 core imports are available in both env and host namespaces: maple_log(ptr,len), maple_error(ptr,len), maple_exit(code), maple_strlen(cstr_ptr), maple_getcwd(out_ptr,out_cap,out_len_ptr), maple_dir_open(path_ptr), maple_dir_read(handle,out_ptr,out_cap,out_len_ptr,out_kind_ptr), maple_dir_close(handle), maple_mkdir(path_ptr).',
        'Use these MapleOS status codes in WAT/WASM examples: 0=MAPLE_OK, 1=MAPLE_ERR_NOT_FOUND, 2=MAPLE_ERR_INVALID_ARG, 3=MAPLE_ERR_ALREADY_EXISTS, 4=MAPLE_ERR_BUFFER_TOO_SMALL, 5=MAPLE_ERR_IO, 6=MAPLE_ERR_BAD_HANDLE.',
        'Path input strings for MapleOS ABI v1 should be null-terminated UTF-8 in exported memory. Output buffer APIs write raw UTF-8 bytes plus an out length; maple_dir_read also writes out_kind where 1=file and 2=directory.',
        'For MapleOS terminal app WAT examples, prefer exporting memory plus a zero-argument main() or _start() function rather than relying on a start section.',
        'When writing WAT string data, do not use C-style escapes like \\0. MapleCode log functions take an explicit byte length, so you usually do not need a null terminator at all unless the import explicitly expects a C string.',
        'If you must encode special bytes inside a WAT string, use valid WAT byte escapes such as \\00, \\0a, or \\22.',
        'MapleCode uses the latest ```wasmname suggestion as the default name when the user runs /run without an explicit argument.',
        'MapleCode uses the latest ```appname suggestion as the default name when the user runs /open-app without an explicit argument.',
        'MapleCode silently auto-runs zero-argument exported _start(), main(), or hello() functions for WAT/WASM examples in its own output pane.',
        'For printable WASM output, prefer env.maple_log(ptr,len) or env.maple_error(ptr,len), export memory, and write UTF-8 bytes into memory before calling the log function.',
        'If you provide a ```wat example, remind the user that MapleCode may already compile and run it automatically for plain build requests, and that /run saves and reruns it silently in MapleCode output.',
        'If you provide a desktop UI app, remind the user that MapleCode may save and open it automatically in a standalone MapleOS app window, and that /open-app saves or reopens it later.',
        'Keep answers practical and terminal-oriented.',
      ].join('\n'),
      role: 'system',
      sender: 'ai',
      timestamp: new Date().toISOString(),
    };

    const conversation = filterTranscriptForModel(transcript)
      .filter((message) => message.role !== 'system' || message.label !== 'session')
      .slice(-16)
      .map<Message>((message) => ({
        id: message.id,
        chatId: 'maplecode',
        content:
          message.role === 'tool'
            ? `[tool:${message.label || 'output'}]\n${message.content}`
            : message.content,
        role: message.role === 'assistant' ? 'assistant' : message.role === 'user' ? 'user' : 'system',
        sender: message.role === 'user' ? 'user' : 'ai',
        timestamp: new Date().toISOString(),
      }));

    return [systemPrompt, ...conversation];
  };

  const runAssistantLoop = async (
    transcriptSeed: MapleCodeMessage[],
    options?: { autoRunWat?: boolean; autoBuildDesktopApp?: boolean },
  ) => {
    let transcript = [...transcriptSeed];
    let watHandled = false;
    let desktopAppHandled = false;
    let autoTerminalSteps = 0;
    let inputCorrectionCount = 0;
    let watCompileCorrectionCount = 0;
    let desktopAppBlockCorrectionCount = 0;

    while (true) {
      const response = await sendMessageToModel(
        buildModelMessages(transcript),
        selectedModel as AIModel,
        activeModels,
        { max_tokens: options?.autoBuildDesktopApp ? 5000 : 900 },
      );

      const assistantContent = response.content || 'MapleCode did not return any content.';
      const assistantMessage = createMessage('assistant', assistantContent);
      transcript = [...transcript, assistantMessage];
      appendMessage(assistantMessage);

      const inputRequest = extractLatestInputRequestBlockFromText(assistantContent, 'assistant');
      if (inputRequest) {
        pendingInputContinuationRef.current = {
          prompt: inputRequest.prompt,
          autoRunWat: options?.autoRunWat === true,
          autoBuildDesktopApp: options?.autoBuildDesktopApp === true,
        };
        appendTerminalPaneEntry('input', `prompt> ${inputRequest.prompt}`);
        setPendingInputPrompt(inputRequest.prompt);
        break;
      }

      if (requestsUnsupportedRuntimeInput(assistantContent)) {
        if (inputCorrectionCount >= 2) {
          break;
        }

        inputCorrectionCount += 1;
        const reminderMessage = createMessage(
          'tool',
          [
            'MapleCode runtime input reminder:',
            'Runtime input APIs such as env.input_utf8, host.input_utf8, stdin, scanf, read_line, and prompt() are not available here.',
            'Ask the user via a fenced ```input block, wait for the reply, then continue the workflow with that value.',
          ].join('\n'),
          'input',
        );
        transcript = [...transcript, reminderMessage];
        appendMessage(reminderMessage);
        continue;
      }

      if (options?.autoRunWat && !watHandled) {
        const watBlock = extractLatestWatBlockFromText(assistantContent, 'assistant');
        if (watBlock) {
          const runResult = await runWatExampleFromBlock(watBlock, 'auto');
          const toolMessage = createMessage('tool', runResult, 'run');
          transcript = [...transcript, toolMessage];
          appendMessage(toolMessage);

          if (isWatCompileFailureResult(runResult)) {
            if (watCompileCorrectionCount >= 2) {
              break;
            }

            watCompileCorrectionCount += 1;
            transcript = [
              ...transcript,
              createMessage(
                'system',
                [
                  'The latest WAT block failed to compile in MapleCode.',
                  runResult,
                  '',
                  'Return a corrected fenced ```wasmname block and a corrected fenced ```wat block.',
                  'Do not explain the fix first. Output the corrected runnable blocks directly.',
                  'Do not use C-style escapes like \\0 in WAT strings. Prefer explicit byte lengths without a null terminator.',
                ].join('\n'),
                'wasm-compile-debug',
              ),
            ];
            continue;
          }

          watHandled = true;
        }
      }

      if (!desktopAppHandled) {
        const htmlBlock = extractLatestHtmlBlockFromText(assistantContent, 'assistant');
        const appNameBlock = extractLatestAppNameBlockFromText(assistantContent, 'assistant');
        const hasTruncatedHtmlFence = hasUnclosedNamedFence(assistantContent, 'html');
        const hasTruncatedAppNameFence = hasUnclosedNamedFence(assistantContent, 'appname');
        const hasIncompleteHtmlBlock = Boolean(htmlBlock) && isLikelyIncompleteHtmlBlock(htmlBlock.html);
        const shouldHandleDesktopApp = Boolean(htmlBlock)
          && !hasIncompleteHtmlBlock
          && (options?.autoBuildDesktopApp || Boolean(appNameBlock));
        console.log('[MapleCodeREPL] runAssistantLoop:desktop-check', {
          autoBuildDesktopApp: options?.autoBuildDesktopApp === true,
          hasHtmlBlock: Boolean(htmlBlock),
          hasAppNameBlock: Boolean(appNameBlock),
          hasTruncatedHtmlFence,
          hasTruncatedAppNameFence,
          hasIncompleteHtmlBlock,
          shouldHandleDesktopApp,
        });

        if (htmlBlock && shouldHandleDesktopApp) {
          const appName = appNameBlock?.name || resolveSuggestedAppName(transcript) || 'latest-app';
          console.log('[MapleCodeREPL] runAssistantLoop:desktop-launch', {
            appName,
            label: htmlBlock.label,
          });
          const runResult = await saveDesktopAppSilently(htmlBlock, appName, 'auto');
          const toolMessage = createMessage('tool', runResult, 'app');
          transcript = [...transcript, toolMessage];
          appendMessage(toolMessage);
          desktopAppHandled = true;
        } else if (
          options?.autoBuildDesktopApp
          && desktopAppBlockCorrectionCount < 3
          && (
            !htmlBlock
            || hasTruncatedHtmlFence
            || hasTruncatedAppNameFence
            || hasIncompleteHtmlBlock
          )
        ) {
          desktopAppBlockCorrectionCount += 1;
          transcript = [
            ...transcript,
            createMessage(
              'system',
              [
                hasTruncatedHtmlFence || hasTruncatedAppNameFence || hasIncompleteHtmlBlock
                  ? 'The last desktop UI app response was incomplete or truncated before MapleCode could save a valid app.'
                  : 'The user asked MapleCode to build a desktop UI app, but your last response did not include a runnable fenced ```html block MapleCode can save and open.',
                'Return exactly one fenced ```appname block and exactly one fenced ```html block for a complete runnable desktop app now.',
                'Output the full app from scratch in one response.',
                'Do not describe the app first. Do not return only prose. Output the runnable blocks directly.',
              ].join('\n'),
              'desktop-app-block-debug',
            ),
          ];
          continue;
        }
      }

      const terminalBlock = extractLatestTerminalCommandBlockFromText(assistantContent, 'assistant');
      if (!terminalBlock) {
        break;
      }

      const autoApprovalMode: 'trust' | 'bypass' | null = bypassEnabled
        ? 'bypass'
        : trustEnabled
          ? 'trust'
          : null;

      if (!autoApprovalMode) {
        const blockedMessage = createMessage(
          'tool',
          [
            'MapleCode detected a fenced ```terminal block, but neither Trust Mode nor Bypass Permissions is enabled so it was not executed.',
            'Turn Trust Mode or Bypass Permissions on, or run it manually with /cmd <command>.',
          ].join('\n\n'),
          'cmd',
        );
        transcript = [...transcript, blockedMessage];
        appendMessage(blockedMessage);
        break;
      }

      if (!runTerminalCommand) {
        const unavailableMessage = createMessage(
          'tool',
          'MapleCode detected a fenced ```terminal block, but the Terminal command bridge is unavailable in this session.',
          'cmd',
        );
        transcript = [...transcript, unavailableMessage];
        appendMessage(unavailableMessage);
        break;
      }

      if (terminalBlock.error || !terminalBlock.commands.length) {
        const invalidBlockMessage = createMessage(
          'tool',
          terminalBlock.error || 'MapleCode could not parse the terminal block.',
          'cmd',
        );
        transcript = [...transcript, invalidBlockMessage];
        appendMessage(invalidBlockMessage);
        break;
      }

      if (autoTerminalSteps >= MAX_AUTO_TERMINAL_STEPS) {
        const limitMessage = createMessage(
          'tool',
          `MapleCode stopped the auto terminal loop after ${MAX_AUTO_TERMINAL_STEPS} steps. Ask for the next command explicitly if you want to continue.`,
          'cmd',
        );
        transcript = [...transcript, limitMessage];
        appendMessage(limitMessage);
        break;
      }

      for (const command of terminalBlock.commands) {
        if (autoTerminalSteps >= MAX_AUTO_TERMINAL_STEPS) {
          const limitMessage = createMessage(
            'tool',
            `MapleCode stopped the auto terminal loop after ${MAX_AUTO_TERMINAL_STEPS} steps. Ask for the next command explicitly if you want to continue.`,
            'cmd',
          );
          transcript = [...transcript, limitMessage];
          appendMessage(limitMessage);
          break;
        }

        const terminalResult = await runTerminalCommandWithPane(command);
        if (terminalResult.kind === 'input') {
          pendingTerminalInputShouldResumeAssistantRef.current = true;
          setPendingTerminalInputPrompt(terminalResult.prompt);
          appendMessage(
            createMessage(
              'tool',
              [
                'Terminal command is waiting for user input.',
                `Command: ${command}`,
                terminalResult.prompt,
              ].join('\n'),
              'cmd',
            ),
          );
          return;
        }

        const terminalToolMessage = createMessage(
          'tool',
          formatAutoTerminalToolMessage(command, terminalResult.text, autoApprovalMode),
          'cmd',
        );
        transcript = [...transcript, terminalToolMessage];
        appendMessage(terminalToolMessage);
        autoTerminalSteps += 1;
      }

      break;
    }
  };

  useEffect(() => {
    const handleWasmRunResult = (event: Event) => {
      const { detail } = event as CustomEvent<MapleCodeWasmRunResultDetail>;
      if (!detail || !pendingWasmRunRootsRef.current.has(detail.projectPath)) {
        return;
      }

      pendingWasmRunRootsRef.current.delete(detail.projectPath);
      const label = detail.status === 'success' ? 'wasm-run' : 'wasm-run';
      const content = [
        `Module Host run result for ${detail.moduleName}:`,
        '',
        ...detail.lines,
      ].join('\n');

      appendMessage(createMessage('tool', content, label));

      if (detail.status === 'error' && !autoDebuggedWasmRunRootsRef.current.has(detail.projectPath) && selectedModel && !isLoadingRef.current) {
        autoDebuggedWasmRunRootsRef.current.add(detail.projectPath);

        window.setTimeout(() => {
          void (async () => {
            setIsLoading(true);
            try {
              const transcriptSeed = [
                ...messagesRef.current,
                createMessage(
                  'system',
                  [
                    'Module Host reported a runtime error for the latest MapleCode example.',
                    `Project path: ${detail.projectPath}`,
                    detail.sourcePath ? `Source path: ${detail.sourcePath}` : '',
                    '',
                    ...detail.lines,
                    '',
                    'Please debug the issue and provide a corrected ```wat block.',
                  ]
                    .filter(Boolean)
                    .join('\n'),
                  'wasm-debug',
                ),
              ];

              await runAssistantLoop(transcriptSeed, { autoRunWat: false });
            } catch (error) {
              appendMessage(
                createMessage(
                  'system',
                  error instanceof Error ? error.message : 'MapleCode failed to debug the WASM runtime error.',
                  'error',
                ),
              );
            } finally {
              setIsLoading(false);
              window.setTimeout(() => inputRef.current?.focus(), 0);
            }
          })();
        }, 0);
      }
    };

    window.addEventListener('maplecode:wasm-run-result', handleWasmRunResult as EventListener);
    return () => window.removeEventListener('maplecode:wasm-run-result', handleWasmRunResult as EventListener);
  }, [activeModels, runAssistantLoop, selectedModel]);

  const submitPrompt = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) {
      return;
    }

    recordInputHistory(trimmed);

    const userMessage = createMessage('user', trimmed);
    appendMessage(userMessage);
    setInput('');

    if (trimmed === 'exit') {
      onExit();
      return;
    }

    if (pendingTerminalInputPrompt) {
      const prompt = pendingTerminalInputPrompt;
      setPendingTerminalInputPrompt(null);

      if (!submitTerminalInput) {
        appendMessage(
          createMessage(
            'system',
            'MapleCode cannot resume terminal input in this session because the Terminal input bridge is unavailable.',
            'error',
          ),
        );
        return;
      }

      setIsLoading(true);
      try {
        const result = await submitTerminalInputWithPane(trimmed);

        if (result.kind === 'input') {
          setPendingTerminalInputPrompt(result.prompt);
          appendMessage(
            createMessage(
              'tool',
              [
                'Terminal still requires input.',
                result.prompt,
              ].join('\n'),
              'cmd',
            ),
          );
          return;
        }

        appendMessage(
          createMessage(
            'tool',
            [
              'Terminal input submitted.',
              `Prompt: ${prompt}`,
              `Answer: ${trimmed}`,
              '',
              result.text || '<no output>',
            ].join('\n'),
            'cmd',
          ),
        );

        if (pendingTerminalInputShouldResumeAssistantRef.current && selectedModel) {
          pendingTerminalInputShouldResumeAssistantRef.current = false;
          await runAssistantLoop(messagesRef.current, { autoRunWat: false, autoBuildDesktopApp: false });
        }
      } catch (error) {
        appendMessage(
          createMessage(
            'system',
            error instanceof Error ? error.message : 'MapleCode failed to resume the terminal command after receiving your input.',
            'error',
          ),
        );
      } finally {
        setIsLoading(false);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }

    if (pendingInputPrompt) {
      const continuation = pendingInputContinuationRef.current;
      setPendingInputPrompt(null);
      pendingInputContinuationRef.current = null;

      if (!selectedModel) {
        appendMessage(
          createMessage(
            'system',
            'No active AI model is available for MapleCode yet. Open model settings or select an active model first.',
            'status',
          ),
        );
        return;
      }

      appendTerminalPaneEntry('input', `input> ${trimmed}`);
      const inputReplyMessage = createMessage(
        'tool',
        [
          'Input reply received.',
          `Prompt: ${continuation?.prompt || pendingInputPrompt}`,
          `Answer: ${trimmed}`,
          continuation?.autoRunWat
            ? 'Continue the existing WAT/WASM workflow with this value and finish with a runnable ```wat block.'
            : continuation?.autoBuildDesktopApp
              ? 'Continue the existing desktop UI app workflow with this value and finish with runnable ```appname and ```html blocks.'
              : 'Continue the existing workflow with this value.',
        ].join('\n'),
        'input',
      );
      appendMessage(inputReplyMessage);

      const transcriptSeed = [...messagesRef.current];
      setIsLoading(true);
      try {
        await runAssistantLoop(transcriptSeed, {
          autoRunWat: continuation?.autoRunWat === true,
          autoBuildDesktopApp: continuation?.autoBuildDesktopApp === true,
        });
      } catch (error) {
        appendMessage(
          createMessage(
            'system',
            error instanceof Error ? error.message : 'MapleCode failed to continue after receiving your input.',
            'error',
          ),
        );
      } finally {
        setIsLoading(false);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }

    if (trimmed.startsWith('/')) {
      const result = await runSlashCommand(trimmed);
      if (result) {
        appendMessage(createMessage('tool', result.content, result.label));
      }
      return;
    }

    if (!selectedModel) {
      appendMessage(
        createMessage(
          'system',
          'No active AI model is available for MapleCode yet. Open model settings or select an active model first.',
          'status',
        ),
      );
      return;
    }

    const shouldAutoRunWat = shouldAutoRunWatRequest(trimmed);
    const shouldAutoBuildDesktopApp = !shouldAutoRunWat && shouldAutoBuildDesktopAppRequest(trimmed);
    const transcriptSeed = messagesRef.current;
    const turnStartIndex = transcriptSeed.length;
    console.log('[MapleCodeREPL] submitPrompt:start', {
      promptPreview: trimmed.slice(0, 160),
      shouldAutoRunWat,
      shouldAutoBuildDesktopApp,
      turnStartIndex,
    });

    setIsLoading(true);
    try {
      await runAssistantLoop(transcriptSeed, {
        autoRunWat: shouldAutoRunWat,
        autoBuildDesktopApp: shouldAutoBuildDesktopApp,
      });

      if (!shouldAutoRunWat) {
        await recoverDesktopAppFromRecentMessages(turnStartIndex);
      }
    } catch (error) {
      appendMessage(
        createMessage(
          'system',
          error instanceof Error ? error.message : 'MapleCode failed to generate a response.',
          'error',
        ),
      );
    } finally {
      setIsLoading(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <div className="flex h-full flex-col text-slate-100">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="font-mono text-sm font-semibold uppercase tracking-[0.2em] text-emerald-300">MapleCode</h2>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                Trust {trustEnabled ? 'on' : 'off'}
              </Badge>
              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                Bypass {bypassEnabled ? 'on' : 'off'}
              </Badge>
            </div>
            <p className="truncate text-xs text-slate-400" title={currentPath}>{sessionMeta.path}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden font-mono text-[11px] text-slate-500 lg:block">
            {sessionMeta.timestamp} · {sessionMeta.elapsed} · {sessionMeta.messageCount} msgs
          </div>
          <Button
            variant="outline"
            className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10"
            onClick={() => setIsTerminalPaneOpen((current) => !current)}
          >
            {isTerminalPaneOpen ? 'Hide output' : 'Show output'}
          </Button>
          <Select value={selectedModel?.id || ''} onValueChange={setSelectedModelId}>
            <SelectTrigger className="w-[220px] border-white/10 bg-white/5 text-slate-100">
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent>
              {activeModels.map((model) => (
                <SelectItem key={model.id} value={model.id}>
                  {model.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10" onClick={onExit}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Exit
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 p-4">
        <Card className="flex h-full min-h-0 flex-col overflow-hidden border-white/10 bg-black/30 shadow-none">
          <div className="min-h-0 flex flex-1">
            <div className={`flex min-h-0 min-w-0 flex-1 flex-col ${isTerminalPaneOpen ? 'border-r border-white/10' : ''}`}>
              <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-4 p-4 font-mono text-sm">
                  {messages.map((message) => (
                    <div key={message.id} className="space-y-1">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        <span>
                          {message.role === 'assistant'
                            ? 'assistant'
                            : message.role === 'user'
                              ? 'user'
                              : message.role === 'tool'
                                ? message.label || 'tool'
                                : message.label || 'system'}
                        </span>
                      </div>
                      <div
                        className={[
                          'rounded-lg border px-3 py-2 leading-6',
                          message.role === 'assistant' && 'border-emerald-400/20 bg-emerald-400/5 text-slate-100',
                          message.role === 'user' && 'border-blue-400/20 bg-blue-400/5 text-slate-100',
                          message.role === 'tool' && 'border-amber-400/20 bg-amber-400/5 text-slate-200',
                          message.role === 'system' && 'border-white/10 bg-white/5 text-slate-300',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {message.role === 'tool' ? (
                          <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-sm leading-6 text-inherit">
                            {message.content}
                          </pre>
                        ) : (
                          <ConsoleMarkdown content={message.content} tone={message.role} />
                        )}
                      </div>
                    </div>
                  ))}

                  {isLoading && (
                    <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 font-mono text-sm text-slate-300">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>
                        {thinkingFrame.before}
                        <span className="text-emerald-300">{thinkingFrame.shimmer}</span>
                        {thinkingFrame.after}
                      </span>
                      <span className="ml-auto text-xs text-slate-500">{sessionMeta.elapsed}</span>
                    </div>
                  )}
                  <div ref={transcriptEndRef} />
                </div>
              </ScrollArea>

              <div className="border-t border-white/10 p-4">
                <div className="mb-2 flex items-center justify-between text-xs text-slate-400">
                  <span>Use /help for tools, or type a normal prompt to chat.</span>
                  <span>Ctrl+C exits · ↑/↓ history</span>
                </div>
                {pendingTerminalInputPrompt && (
                  <div className="mb-3 rounded-md border border-cyan-400/20 bg-cyan-400/10 px-3 py-2 font-mono text-sm text-cyan-100">
                    <div className="mb-1 text-[11px] uppercase tracking-[0.18em] text-cyan-300">Terminal input required</div>
                    <div className="whitespace-pre-wrap break-words">{pendingTerminalInputPrompt}</div>
                  </div>
                )}
                {pendingInputPrompt && (
                  <div className="mb-3 rounded-md border border-amber-400/20 bg-amber-400/10 px-3 py-2 font-mono text-sm text-amber-100">
                    <div className="mb-1 text-[11px] uppercase tracking-[0.18em] text-amber-300">Awaiting input</div>
                    <div className="whitespace-pre-wrap break-words">{pendingInputPrompt}</div>
                  </div>
                )}
                <form
                  className="flex items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void submitPrompt();
                  }}
                >
                  <div className="flex h-10 items-center rounded-md border border-white/10 bg-white/5 px-3 text-emerald-300">
                    <TerminalSquare className="h-4 w-4" />
                  </div>
                  <Input
                    ref={inputRef}
                    value={input}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setInput(nextValue);
                      if (historyIndex !== -1) {
                        setHistoryIndex(-1);
                      }
                      historyDraftRef.current = nextValue;
                    }}
                    onKeyDown={handleInputKeyDown}
                    placeholder={pendingTerminalInputPrompt || pendingInputPrompt ? 'Reply with the requested input…' : 'Ask MapleCode something, or run /ls, /run, /status…'}
                    className="border-white/10 bg-white/5 font-mono text-slate-100 placeholder:text-slate-500"
                    disabled={isLoading}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Button type="submit" disabled={isLoading || !input.trim()}>
                    {pendingTerminalInputPrompt || pendingInputPrompt ? 'Reply' : 'Send'}
                  </Button>
                </form>
              </div>
            </div>

            <div
              className={[
                'min-h-0 overflow-hidden transition-all duration-300 ease-out',
                isTerminalPaneOpen
                  ? 'w-[40%] min-w-[320px] max-w-[560px] opacity-100'
                  : 'w-0 min-w-0 opacity-0',
              ].join(' ')}
            >
              <div className="flex h-full min-h-0 flex-col bg-black/60">
                <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                  <div>
                    <div className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Terminal output</div>
                    <div className="text-xs text-slate-500">MapleCode command bridge</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-300">
                      {terminalPaneEntries.length} entries
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10"
                      onClick={clearTerminalPane}
                      disabled={!terminalPaneEntries.length}
                    >
                      Clear
                    </Button>
                  </div>
                </div>

                <ScrollArea className="min-h-0 flex-1">
                  <div className="space-y-4 p-4 font-mono text-sm">
                    {!terminalPaneEntries.length ? (
                      <div className="rounded-lg border border-dashed border-white/10 bg-white/5 px-3 py-4 text-sm text-slate-500">
                        Terminal command output will appear here when MapleCode runs commands, compiles WAT, executes runtime output, or asks for input.
                      </div>
                    ) : (
                      ([
                        {
                          title: 'Command',
                          kind: 'command' as const,
                          empty: 'No commands yet.',
                          className: 'border-emerald-400/20 bg-emerald-400/5 text-emerald-200',
                        },
                        {
                          title: 'Compile',
                          kind: 'compile' as const,
                          empty: 'No compile output yet.',
                          className: 'border-amber-400/20 bg-amber-400/10 text-amber-100',
                        },
                        {
                          title: 'Runtime',
                          kind: 'runtime' as const,
                          empty: 'No runtime output yet.',
                          className: 'border-white/10 bg-white/5 text-slate-200',
                        },
                        {
                          title: 'Input/Prompt',
                          kind: 'input' as const,
                          empty: 'No prompts yet.',
                          className: 'border-cyan-400/20 bg-cyan-400/10 text-cyan-100',
                        },
                      ]).map((section) => {
                        const entries = terminalPaneEntries.filter((entry) => entry.kind === section.kind);

                        return (
                          <div key={section.kind} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                {section.title}
                              </div>
                              <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px] text-slate-400">
                                {entries.length}
                              </Badge>
                            </div>

                            {entries.length ? (
                              entries.map((entry) => (
                                <div
                                  key={entry.id}
                                  className={[
                                    'rounded-lg border px-3 py-2',
                                    section.className,
                                  ].join(' ')}
                                >
                                  <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-sm leading-6 text-inherit">
                                    {entry.content}
                                  </pre>
                                </div>
                              ))
                            ) : (
                              <div className="rounded-lg border border-dashed border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-500">
                                {section.empty}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                    <div ref={terminalPaneEndRef} />
                  </div>
                </ScrollArea>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MapleCodeREPL;